import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import apiClient from '../api/client';
import PageTransitionWrapper from '../components/layout/PageTransitionWrapper';
import Container from '../components/layout/Container';

import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import DownloadCards from '../components/common/DownloadCard';

import HomeBanner from '../components/home/HomeBanner';
import Info from '../components/common/Info';
import BannerSlider from '../components/home/BannerSlider';
import Offering from '../components/home/Offering';
import LatestYouTubeVideos from '../components/home/LatestYoutube';

import ShalomSection from '../components/home/ShalomSection';
import OfficeHours from '../components/home/OfficeHours';

import EventCalendar from '../components/schedules/EventCalendar';
import WorshipSchedule from '../components/schedules/WorshipSchedule';

import { DocumentTextIcon, UsersIcon, ChatBubbleOvalLeftEllipsisIcon, HeartIcon, PhoneIcon, ShieldCheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const quickLinks = [
    { title: 'All In One Info', link: 'https://linktr.ee/kasihkaruniabadung?lt_utm_source=lt_share_link#450474995', icon: InformationCircleIcon },
    { title: 'Form Jemaat Baru', link: '/informasi-formulir/JemaatBaru', icon: UsersIcon },
    { title: 'Form Baptisan', link: '/informasi-formulir/Baptisan', icon: DocumentTextIcon },
    { title: 'Form Katekisasi', link: '/informasi-formulir/Katekisasi', icon: ChatBubbleOvalLeftEllipsisIcon },
    { title: 'Pemberkatan Perkawinan', link: '/informasi-formulir/Perkawinan', icon: HeartIcon },
    { title: 'Kontak Korsek', link: '/informasi-formulir/kontak-korsek', icon: PhoneIcon },
    { title: 'PHMJ', link: '/informasi-formulir/phmj', icon: ShieldCheckIcon },
];

const cleanHtml = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.innerText.trim();
};

const Home = () => {
    const [latestObituary, setLatestObituary] = useState(null);
    const [latestYoutubeVideo, setLatestYoutubeVideo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isCancelled = false;
        let progressInterval;
        const MIN_LOADING_TIME = 1000;
        const DELAY_AFTER_LOAD = 700;

        const fetchData = async () => {
            setIsLoading(true);
            setProgress(0);
            setError(null);

            const startTime = Date.now();
            progressInterval = setInterval(() => {
                const elapsedTime = Date.now() - startTime;
                let calculatedProgress = (elapsedTime / MIN_LOADING_TIME) * 100;
                if (calculatedProgress > 99) calculatedProgress = 99;
                if (!isCancelled) {
                    setProgress(Math.floor(calculatedProgress));
                }
            }, 50);

            try {
                const [homeContentRes, youtubeVideoRes] = await Promise.all([
                    apiClient.getHomeContent(),
                    apiClient.getLatestYoutubeVideo(),
                    new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME))
                ]);

                if (!isCancelled) {
                    if (homeContentRes && homeContentRes.berita_duka) {
                        setLatestObituary(homeContentRes.berita_duka);
                    } else {
                        setLatestObituary(null);
                    }
                    if (youtubeVideoRes) {
                        setLatestYoutubeVideo(youtubeVideoRes);
                    } else {
                        setLatestYoutubeVideo(null);
                    }
                }
            } catch (err) {
                if (!isCancelled) {
                    setError('Gagal mengambil data atau terjadi timeout.');
                    console.error('API Fetch Error:', err);
                }
            } finally {
                clearInterval(progressInterval);
                if (!isCancelled) {
                    setProgress(100);
                    setTimeout(() => {
                        setIsLoading(false);
                    }, DELAY_AFTER_LOAD);
                }
            }
        };

        fetchData();

        return () => {
            isCancelled = true;
            clearInterval(progressInterval);
        };
    }, []);

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <ErrorDisplay message={error} />
            </div>
        );
    }

    return (
        <div className="bg-gray-100 min-h-screen font-sans antialiased">
            {isLoading ? (
                <LoadingSpinner progress={progress} />
            ) : (
                <>
                    <PageTransitionWrapper>
                        <section className="mb-4 mt-4">
                            <HomeBanner />
                        </section>

                        <Container>
                            <div className="mb-4">
                                <Info />
                            </div>

                            <section className="mb-4">
                                <BannerSlider />
                            </section>

                            <section className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6">
                                <ShalomSection />
                                <OfficeHours />
                            </section>

                            <section className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6">
                                <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200 text-center">
                                    <h2 className="text-3xl font-bold text-gray-800 mb-4 pb-2 border-b-4 border-blue-600">
                                        DOWNLOAD
                                    </h2>
                                    <DownloadCards />
                                </div>
                                <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200 text-center">
                                    <h2 className="text-3xl font-bold text-gray-800 mb-4 pb-2 border-b-4 border-blue-600">
                                        JADWAL IBADAH
                                    </h2>
                                    <WorshipSchedule />
                                </div>
                            </section>
                            <section className="mt-4">
                                <EventCalendar />
                            </section>

                            <section className="mt-4 pb-8 grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">
                                <div className="flex flex-col gap-4 h-full">
                                    <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
                                        <h2 className="text-3xl text-center font-bold text-gray-800 mb-4 pb-2 border-b-4 border-blue-600">
                                            INFORMASI & FORMULIR
                                        </h2>
                                        <div className="grid grid-cols-1 gap-2">
                                            {quickLinks.map((item, index) => {
                                                const IconComponent = item.icon;
                                                return (
                                                    <Link
                                                        key={index}
                                                        to={item.link}
                                                        className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-300 transform hover:bg-blue-100 hover:border-blue-300 hover:shadow-md hover:scale-105"
                                                    >
                                                        {IconComponent && <IconComponent className="h-6 w-6 text-blue-600 mr-4 flex-shrink-0" />}
                                                        <span className="font-medium text-gray-800 text-lg">{item.title}</span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                        <hr className="my-6 border-gray-200" />
                                        <div className="text-center">
                                            <Link
                                                to="/berita-duka"
                                                className="inline-block px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-full hover:bg-blue-700 transition-colors duration-300"
                                            >
                                                Lihat Semua
                                            </Link>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200">
                                        <h2 className="text-3xl font-bold text-center text-gray-800 mb-4 pb-2 border-b-4 border-blue-600">
                                            PERSEMBAHAN
                                        </h2>
                                        <Offering />
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200 h-full">
                                    <h2 className="text-3xl font-bold text-center text-gray-800 mb-4 pb-2 border-b-4 border-blue-600">
                                        BERITA DUKA
                                    </h2>
                                    <div className="mt-6 text-center">
                                        <Link
                                            to="/berita-duka" // Ganti dengan path yang sesuai
                                            className="inline-block px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-full hover:bg-blue-700 transition-colors duration-300"
                                        >
                                            Lihat Semua
                                        </Link>
                                    </div>
                                    <div className="space-y-2 mt-4">
                                        {latestObituary ? (
                                            <div className="flex flex-col items-center p-2 rounded-lg -mx-2">
                                                <img
                                                    src={latestObituary.image}
                                                    alt={latestObituary.berita}
                                                    className="w-full h-65 object-cover rounded-xl mb-2 border-2 border-gray-300"
                                                />
                                                <div className="flex-grow text-center">
                                                    <h3 className="font-semibold text-gray-800">
                                                        {cleanHtml(latestObituary.berita)}
                                                    </h3>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        Tanggal: {new Date(latestObituary.created_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 text-center">Tidak ada berita duka terbaru.</p>
                                        )}
                                    </div>
                                </div>
                            </section>

                            <section className="mt-4 pb-8">
                                <LatestYouTubeVideos videos={latestYoutubeVideo} />
                            </section>
                        </Container>
                    </PageTransitionWrapper>
                </>
            )}
        </div>
    );
};

export default Home;